// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// HAZARD × CATEGORY, encoded as observed-minus-expected rather than raw count.
//
// A raw-count heatmap of this data would say one thing over and over: the
// biggest categories have the most of everything. Shading the RESIDUAL — how
// far a pairing sits from what you would expect if hazards were spread evenly
// across categories — removes size from the picture and leaves the actual
// finding: where a hazard is concentrated somewhere surprising.
//
// A diverging ramp is used because the interesting values run in both
// directions: a pairing that is strikingly ABSENT is as informative as one that
// is over-represented.

import { getBundle } from '../data';
import { esc, num } from '../format';
import { navigate } from '../router';
import { chartFrame, labelChart, markTip, svgEl } from '../components/svg';
import { glossaryButton } from '../glossary';

const NON_CATEGORY_TERMS = new Set([40159]);

type Mode = 'residual' | 'count';

export function renderMatrix(host: HTMLElement): void {
  const { stats, hazards, termById } = getBundle();
  let mode: Mode = 'residual';

  host.innerHTML = `
    <div class="view-head">
      <h2>Which hazards attach to which parts of the shelf</h2>
      <p>Every top-level product category against every hazard class. By default the colour shows
      ${glossaryButton('expected', 'observed versus expected')} — how far a pairing sits from what its size alone
      would predict — because a raw count would simply redraw which categories are biggest.</p>
    </div>
    <div class="controls">
      <div class="control">
        <span class="field-label" id="mode-label">Colour shows</span>
        <div class="row" role="group" aria-labelledby="mode-label" style="gap:var(--space-xs)">
          <button type="button" class="btn btn-sm is-active" data-mode="residual" aria-pressed="true">Over / under-represented</button>
          <button type="button" class="btn btn-sm" data-mode="count" aria-pressed="false">Raw count</button>
        </div>
      </div>
    </div>
    <div id="matrix"></div>
    <div class="legend" id="mlegend"></div>
    <div class="note caveat">
      A recall usually belongs to more than one category, so it is counted once in each top-level category it
      carries. The cells therefore add to more than the number of recalls — deliberately, and the pipeline
      asserts that relationship on every build.
    </div>
  `;

  const mount = host.querySelector<HTMLElement>('#matrix')!;
  const legend = host.querySelector<HTMLElement>('#mlegend')!;

  const cats = stats.matrix.cats.filter((c) => !NON_CATEGORY_TERMS.has(c));
  const cellFor = new Map(stats.matrix.cells.map((c) => [`${c.cat}|${c.hazard}`, c]));

  const draw = () => {
    mount.innerHTML = '';
    mount.append(grid(cats, hazards, cellFor, termById, mode));
    legend.innerHTML = mode === 'residual'
      ? `<span class="legend-item"><span class="legend-swatch" style="background:${RAMP_LOW}"></span>Fewer than expected</span>
         <span class="legend-item"><span class="legend-swatch" style="background:${MID}"></span>About as expected</span>
         <span class="legend-item"><span class="legend-swatch" style="background:${RAMP_HIGH}"></span>More than expected</span>
         <span class="legend-item muted">Cell size is fixed; only colour carries the value.</span>`
      : `<span class="legend-item"><span class="legend-swatch" style="background:${MID}"></span>Few recalls</span>
         <span class="legend-item"><span class="legend-swatch" style="background:${RAMP_HIGH}"></span>Many recalls</span>`;
  };

  for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
    btn.addEventListener('click', () => {
      mode = btn.dataset.mode as Mode;
      for (const b of host.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      }
      draw();
    });
  }
  draw();
}

const RAMP_LOW = '#3f6fa8';
const MID = '#f2efe9';
const RAMP_HIGH = '#c2410c';

function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * Math.max(0, Math.min(1, t))));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

const ROW_H = 34;
const LABEL_W = 210;
const COL_W = 82;

function grid(
  cats: number[],
  hazards: ReturnType<typeof getBundle>['hazards'],
  cellFor: Map<string, { observed: number; expected: number }>,
  termById: ReturnType<typeof getBundle>['termById'],
  mode: Mode,
): HTMLElement {
  const W = LABEL_W + hazards.length * COL_W + 12;
  const HEAD = 74;
  const H = HEAD + cats.length * ROW_H + 10;

  const { figure, mount } = chartFrame({
    title: mode === 'residual' ? 'Over- and under-represented pairings' : 'Recalls per category and hazard',
    subtitle: 'Rows are product categories, columns are hazards. Click a cell to see those recalls.',
    scroll: true,
    minWidth: 720,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMinYMin meet' });
  svg.style.minWidth = `${W}px`;
  labelChart(svg, 'Hazard by product category matrix',
    mode === 'residual'
      ? 'Colour shows how far each pairing sits above or below what its size would predict.'
      : 'Colour shows the number of recalls in each pairing.');

  // Column headers, angled so long hazard names do not collide.
  hazards.forEach((hz, i) => {
    const cx = LABEL_W + i * COL_W + COL_W / 2;
    const t = svgEl('text', {
      x: cx, y: HEAD - 10, class: 'axis-text', 'text-anchor': 'start',
      transform: `rotate(-38 ${cx} ${HEAD - 10})`,
    });
    t.textContent = hz.label.length > 20 ? `${hz.label.slice(0, 19)}…` : hz.label;
    svg.append(t);
  });

  const maxCount = Math.max(1, ...[...cellFor.values()].map((c) => c.observed));
  // Symmetric residual scale so the two directions are comparable.
  const maxResid = Math.max(1, ...[...cellFor.values()].map((c) => Math.abs(c.observed - c.expected)));

  cats.forEach((cat, r) => {
    const y = HEAD + r * ROW_H;
    const term = termById.get(cat);
    const label = svgEl('text', { x: LABEL_W - 8, y: y + ROW_H / 2 + 4, class: 'axis-text', 'text-anchor': 'end' });
    label.textContent = term?.name ?? String(cat);
    svg.append(label);

    hazards.forEach((hz, i) => {
      const c = cellFor.get(`${cat}|${hz.id}`);
      const observed = c?.observed ?? 0;
      const expected = c?.expected ?? 0;
      const resid = observed - expected;

      let fill = MID;
      if (mode === 'count') {
        fill = observed ? mix(MID, RAMP_HIGH, Math.sqrt(observed / maxCount)) : 'var(--bg-elevated)';
      } else if (observed || expected >= 0.5) {
        const t = Math.min(1, Math.abs(resid) / maxResid);
        fill = mix(MID, resid >= 0 ? RAMP_HIGH : RAMP_LOW, Math.sqrt(t));
      } else {
        fill = 'var(--bg-elevated)';
      }

      const g = svgEl('g', { class: 'mark', role: 'button', tabindex: '0', style: 'cursor:pointer' });
      g.append(svgEl('rect', {
        x: LABEL_W + i * COL_W + 1, y: y + 1, width: COL_W - 2, height: ROW_H - 2, rx: 3,
        fill, stroke: 'var(--bg-base)', 'stroke-width': 1,
      }));

      if (observed > 0) {
        const val = svgEl('text', {
          x: LABEL_W + i * COL_W + COL_W / 2, y: y + ROW_H / 2 + 4,
          'text-anchor': 'middle', 'font-size': 11.5, 'pointer-events': 'none',
          // Chosen against the cell fill rather than blended into it: a
          // mix-blend-mode here darkens numerals into their own background on
          // the strongest cells.
          fill: contrastText(fill),
          'font-variant-numeric': 'tabular-nums',
        });
        val.textContent = num(observed);
        g.append(val);
      }

      markTip(g,
        `${term?.name ?? cat} × ${hz.label}\n${num(observed)} recall${observed === 1 ? '' : 's'}\n`
        + `Expected if hazards were spread evenly: ${expected.toFixed(1)}\n`
        + `${resid >= 0 ? '+' : ''}${resid.toFixed(1)} versus expected`,
        `${term?.name ?? cat} and ${hz.label}: ${num(observed)} recalls, ${resid >= 0 ? 'above' : 'below'} expected. Search these.`);

      const go = () => navigate('search', { c: cat, h: hz.id });
      g.addEventListener('click', go);
      g.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
      });
      svg.append(g);
    });
  });

  void esc;
  mount.append(svg);
  return figure;
}

/** Black or white, whichever actually reads on the given fill. */
function contrastText(hex: string): string {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 'var(--text-primary)';
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  /*
   * 0.2 is the crossover where black and white are equally legible. The previous
   * 0.42 handed white text to every cell in the 0.183–0.42 band, where white
   * reaches only 2.25:1 — below AA and below AA Large — while black would have
   * been comfortably above it.
   */
  return l > 0.2 ? '#1c1a17' : '#ffffff';
}
